import { Classes, Icon, Intent, Tree, type TreeNodeInfo } from "@blueprintjs/core"
import { useCallback, useMemo, useState } from "react"

export type FilterItem = {
  id: string
  label: string
  children?: FilterItem[]
}

type FilterTreeProps = {
  data: FilterItem[]
  selectedFilters: Record<string, Array<string | number>>
  onSelectedFilterChange: (next: Record<string, Array<string | number>>) => void
}

export const FilterTree = ({ data, selectedFilters, onSelectedFilterChange }: FilterTreeProps) => {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())

  const collectIds = useCallback((item: FilterItem): string[] => {
    const ids = [item.id]
    if (item.children) {
      for (const child of item.children) {
        ids.push(...collectIds(child))
      }
    }
    return ids
  }, [])

  const collectLeafValueIds = useCallback((item: FilterItem): Array<string | number> => {
    const values: Array<string | number> = []
    const hasChildren = (item.children?.length ?? 0) > 0
    if (!hasChildren) {
      values.push(item.id)
    } else {
      for (const child of item.children!) {
        values.push(...collectLeafValueIds(child))
      }
    }
    return values
  }, [])

  const toggleGroup = useCallback(
    (groupId: string | number, leafIds: Array<string | number>) => {
      const key = String(groupId)
      const current = new Set(selectedFilters[key] ?? [])
      const allSelected = leafIds.length > 0 && leafIds.every(id => current.has(id))
      const nextArray = allSelected
        ? Array.from(new Set([...current].filter(id => !leafIds.includes(id))))
        : Array.from(new Set([...current, ...leafIds]))

      const nextFilters = { ...selectedFilters }
      if (nextArray.length === 0) {
        delete nextFilters[key]
      } else {
        nextFilters[key] = nextArray
      }
      onSelectedFilterChange(nextFilters)
    },
    [selectedFilters, onSelectedFilterChange],
  )

  const toggleLeaf = useCallback(
    (groupId: string | number, valueId: string | number) => {
      const key = String(groupId)
      const current = new Set(selectedFilters[key] ?? [])
      if (current.has(valueId)) {
        current.delete(valueId)
      } else {
        current.add(valueId)
      }
      const nextFilters = { ...selectedFilters }
      if (current.size === 0) {
        delete nextFilters[key]
      } else {
        nextFilters[key] = Array.from(current)
      }
      onSelectedFilterChange(nextFilters)
    },
    [selectedFilters, onSelectedFilterChange],
  )

  const handleNodeExpand = useCallback((node: TreeNodeInfo) => {
    setExpanded(prev => {
      const next = new Set(prev)
      next.add(node.id as string)
      return next
    })
  }, [])

  const handleNodeCollapse = useCallback((node: TreeNodeInfo) => {
    setExpanded(prev => {
      const next = new Set(prev)
      next.delete(node.id as string)
      return next
    })
  }, [])

  const buildNodes = useCallback(
    (items: FilterItem[], currentGroupId?: string | number): Array<TreeNodeInfo> => {
      return items.map(item => {
        const hasChildren = (item.children?.length ?? 0) > 0
        const isExpanded = expanded.has(item.id)
        const isGroup = hasChildren

        let iconNode: JSX.Element | undefined
        let childNodes: Array<TreeNodeInfo> | undefined
        const label = item.label

        if (isGroup) {
          const leafIds = collectLeafValueIds(item)
          const key = String(item.id)
          const selected = new Set(selectedFilters[key] ?? [])
          const allChecked = leafIds.length > 0 && leafIds.every(id => selected.has(id))
          const someChecked = !allChecked && leafIds.some(id => selected.has(id))
          iconNode = (
            <Icon
              className={Classes.TREE_NODE_ICON}
              icon={allChecked ? "selection" : someChecked ? "minus" : "circle"}
              intent={allChecked || someChecked ? Intent.PRIMARY : Intent.NONE}
              onClick={e => {
                e.stopPropagation()
                toggleGroup(item.id, leafIds)
              }}
              title={allChecked ? "Uncheck group" : "Check group"}
            />
          )
          childNodes = hasChildren ? buildNodes(item.children!, item.id) : undefined
        } else if (!hasChildren && currentGroupId != null) {
          const key = String(currentGroupId)
          const selected = new Set(selectedFilters[key] ?? [])
          const isChecked = selected.has(item.id)
          iconNode = (
            <Icon
              className={Classes.TREE_NODE_ICON}
              icon={isChecked ? "selection" : "circle"}
              intent={isChecked ? Intent.PRIMARY : Intent.NONE}
              onClick={e => {
                e.stopPropagation()
                toggleLeaf(currentGroupId, item.id)
              }}
              title={isChecked ? "Uncheck" : "Check"}
            />
          )
        } else if (hasChildren) {
          // Non-group parent; just recurse without selection logic
          childNodes = buildNodes(item.children!, currentGroupId)
        }

        return {
          id: item.id,
          label,
          icon: iconNode,
          hasCaret: hasChildren,
          isExpanded: hasChildren ? isExpanded : undefined,
          childNodes,
        }
      })
    },
    [expanded, collectLeafValueIds, selectedFilters, toggleGroup, toggleLeaf],
  )

  const contents = useMemo(() => buildNodes(data), [buildNodes, data])

  return (
    <Tree contents={contents} onNodeExpand={handleNodeExpand} onNodeCollapse={handleNodeCollapse} />
  )
}
